/* eslint-disable no-param-reassign */
import React, { useContext } from 'react';

// Custom Components

// OL
import OlBaseLayer from 'ol/layer/Base';
import OlTileLayer from 'ol/layer/Tile';
import OlImageLayer from 'ol/layer/Image';
import OlTile from 'ol/Tile';

import OlTileWMSSource from 'ol/source/TileWMS';
import OlWMTSTileGrid from 'ol/tilegrid/WMTS';
import OlWMTSSource from 'ol/source/WMTS';
import OlTMSSource from 'ol/source/XYZ';
import OlVectorLayer from 'ol/layer/Vector';

// Types
// import TileState from 'ol/TileState';
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';
import ImageLayer from '@/components/Map/Layers/ImageLayer';
import TileLayer from '@/components/Map/Layers/TileLayer';
import VectorLayer from '@/components/Map/Layers/VectorLayer';
import { getStyleByName } from '../../mapStyles';

const GSURL = process.env.REACT_APP_GEOSERVERPATH;

type GeoLayerProps = {
  id: string;
  // key: number | string;
  layer: OlBaseLayer;
  visible?: boolean;
  swipeValue?: number | null
  swipeState?: "L" | "R"
};

/* type StorageType = {
  [key: string]: any;
}; */

const GeoLayer = (props: GeoLayerProps) => {
  const { id, layer, visible, swipeValue, swipeState } = props;
  const mapContext = useContext<MapContextType | null>(MapContext);

  // const storageRef = useRef<StorageType>({});

  const checkIfVisible = (): boolean => {
    const layerId = id;
    const defaultVisible = layer.get('visible');

    // 1. check if visible is explicitly set
    if (visible === true || visible === false) {
      return visible;
    }

    // 2. check local storage
    const contextVisibility = mapContext?.layerVisibility;
    if (contextVisibility) {
      const hasKey = contextVisibility[layerId] !== undefined;
      if (hasKey) {
        const contextVisible = contextVisibility[layerId];
        if (contextVisible === false || contextVisible === true) {
          return contextVisible;
        }
      }
    }

    // 3. check DB default
    if (defaultVisible === false || defaultVisible === true) {
      return defaultVisible;
    }

    // 4. Application default
    return true;
  }
   
  const layerVisible = checkIfVisible();

  // useEffect(() => {
  //   if (mapContext?.map) {
  //     const visible = checkIfVisible();
  //     layer.setVisible(visible);
  //     mapContext.map.addLayer(layer);
  //   }

  //   return () => {
  //     if (mapContext?.map) {
  //       const mapLayer = findLayer(mapContext.map, props.id);
  //       if (mapLayer) {
  //         mapContext.map.removeLayer(mapLayer);
  //       }
  //     }
  //   };
  // }, [mapContext?.map]);

  // useEffect(() => {
  //   if (mapContext?.map) {
  //     const mapLayer = findLayer(mapContext.map, props.id);
  //     if (mapLayer) {
  //       const isVisible = visible ? true : false;
  //       mapLayer.setVisible(isVisible);
  //     }
  //   }
  // }, [visible]);

  /* const customLoader = (tile: OlTile, src: string) => {
    if (storageRef.current[src]) {
      // @ts-ignore
      tile.getImage().src = storageRef.current[src];
    } else {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.addEventListener('loadend', function (evt) {
        const data = this.response;
        if (data !== undefined) {
          // @ts-ignore
          tile.getImage().src = URL.createObjectURL(data);
        } else {
          tile.setState(TileState.ERROR);
        }
      });
      xhr.addEventListener('error', () => {
        tile.setState(TileState.ERROR);
      });
      xhr.addEventListener('load', function () {
        const data = this.response;
        if (data !== undefined) {
          storageRef.current[src] = data;
        }
      });
      xhr.open('GET', src);
      xhr.send();
    }
  }; */

  // const useCache = layer.get('use_cache') === true;

  const isTileLayer = layer instanceof OlTileLayer;
  const isImageLayer = layer instanceof OlImageLayer;
  const isVectorLayer = layer instanceof OlVectorLayer;

  const isTileWMSSource = isTileLayer
    ? layer.getSource() instanceof OlTileWMSSource
    : false;
  const isTileWMTSSource = isTileLayer
    ? layer.getSource() instanceof OlWMTSSource
    : false;
  const isTileTMSSource = isTileLayer
    ? layer.getSource() instanceof OlTMSSource
    : false;

  const layersTime = mapContext?.layerTime;

  const timeseries = layer.get('timeseries');
  const hasSlider = !!timeseries;

  const time =
    layersTime && layersTime[id] && hasSlider ? layersTime[id] : undefined;

  let minZoom = layer.get('visible_min_zoom');
  if (minZoom !== undefined && minZoom > 0) {
    minZoom -= 1; // exclusive > inclusive limit
  }
  const maxZoom = layer.get('visible_max_zoom');

  const layerUrl = layer.get('url');

  const hasCustomLayerUrl: boolean = (layerUrl && layerUrl !== '');

  const isExternal = hasCustomLayerUrl && layerUrl.indexOf('http') === 0
  const isInternal = !isExternal;

  const url = hasCustomLayerUrl ? layerUrl : GSURL;

  if (isTileLayer)
    return (
      <TileLayer
        id={id}
        visible={layerVisible}
        zIndex={layer.get('z_index')}
        extent={layer.get('extent')}
        minZoom={minZoom}
        maxZoom={maxZoom}
        swipeValue={swipeValue}
        swipeState={swipeState}
        wms={
          isTileWMSSource
            ? {
                url,
                serverType: 'geoserver',
                // tileLoadFunction: customLoader,
                // tileLoadFunction: useCache ? customLoader : undefined,
                params: {
                  LAYERS: layer.get('layer'),
                  FORMAT: layer.get('format'),
                  TILED: layer.get('tiled'),
                  STYLES: layer.get('styles'),
                  authkey: isInternal ? (localStorage.getItem('auth_token') || undefined) : undefined,
                  VIEWPARAMS: isInternal ? `authkey:${localStorage.getItem('auth_token')}` : undefined,
                  TIME: time,
                  CQL_FILTER: layer.get('cql_filter') || undefined,
                },
              }
            : undefined
        }
        wmts={
          isTileWMTSSource
            ? {
                url,
                layer: layer.get('layer'),
                // tileLoadFunction: customLoader,
                style: layer.get('styles'),
                format: layer.get('format'),
                matrixSet: (layer.getSource() as OlWMTSSource).getMatrixSet(),
                tileGrid: (
                  layer.getSource() as OlWMTSSource
                ).getTileGrid() as OlWMTSTileGrid,
                tileLoadFunction: (imageTile: OlTile, src: string) => {
                  // @ts-ignore
                  imageTile.getImage().src = src + (isInternal ? `&authkey=${localStorage.getItem("auth_token")}` : "") ;
                },
                dimensions: time ? { TIME: time } : undefined,
              }
            : undefined
        }
        tms={
          isTileTMSSource
            ? {
                url,
                // tileLoadFunction: customLoader,
              }
            : undefined
        }
      />
    );
  if (isVectorLayer)
    return (
      <VectorLayer
        id={id}
        visible={layerVisible}
        zIndex={layer.get('z_index')}
        extent={layer.get('extent')}
        minZoom={minZoom}
        maxZoom={maxZoom}
        swipeValue={swipeValue}
        swipeState={swipeState}
        source={layer.getSource()}
        style={getStyleByName(layer.get('ui_style'))}
      />
    );
  if (isImageLayer)
    return (
      <ImageLayer
        id={id}
        visible={layerVisible}
        zIndex={layer.get('z_index')}
        extent={layer.get('extent')}
        minZoom={minZoom}
        maxZoom={maxZoom}
        swipeValue={swipeValue}
        swipeState={swipeState}
        wms={{
          url,
          serverType: 'geoserver',
          ratio: 1.2,
          params: {
            LAYERS: layer.get('layer'),
            FORMAT: layer.get('format'),
            STYLES: layer.get("styles"),
            authkey: isInternal ? (localStorage.getItem('auth_token') || undefined) : undefined,
            viewparams: typeof layer.get('params') === 'object'
            ? Object.entries(layer.get('params'))
                .map(([key, val]) => `${key}:${val}`)
                .join(';')
            : undefined,
            CQL_FILTER: layer.get("cql_filter"),
            TIME: time,
          },
           imageLoadFunction: (image, src) => {
            const offsetY = layer.get('offset_y');
            const offsetX = layer.get('offset_x');
          
            if (!(offsetX || offsetY)) {
              // @ts-ignore
              image.getImage().src = src; // Default function if no offset given
              return;
            }
          
            try {
              // Build full URL using the current host and the given src
              const baseUrl = window.location.origin;
              const fullUrl = new URL(src, baseUrl);
          
              // Extract and parse the BBOX parameter
              const bboxParam = fullUrl.searchParams.get('BBOX');
              if (!bboxParam) return;
          
              const bbox = bboxParam.split(',').map(Number);
              if (bbox.length !== 4 || bbox.some(Number.isNaN)) return;
          
              // Apply the offset
              const newBbox = [
                bbox[0] + offsetX,
                bbox[1] + offsetY,
                bbox[2] + offsetX,
                bbox[3] + offsetY
              ];
          
              fullUrl.searchParams.set('BBOX', newBbox.join(','));
          
              // Load the image using the modified URL
              // @ts-ignore
              image.getImage().src = fullUrl.toString();
            } catch (err) {
              console.error('Failed to modify image URL:', err);
              // @ts-ignore
              image.getImage().src = src;
            }
          }
        }}
      />
    );
  return null;
};

export default GeoLayer;
